import { LatLng, LatLngExpression, LeafletEventHandlerFnMap } from "leaflet";
import { Polyline, Polygon } from "react-leaflet";
import { BLUE_HIGHLIGHT, DEFAULT_ANNOTATION_COLOR } from "../../../constants/colors";
import { ShapeLineWidth } from "../../../constants/annotation";

import { point, Units } from "@turf/helpers";
import bearing from "@turf/bearing";
import destination from "@turf/destination";

interface ArrowProps {
  positions: LatLng[];
  isHighlighted?: boolean;
  interactive: boolean;
  easyClickTolerance: number;
  color?: string;
  opacity?: number;
  id?: string;
  zoomLevel?: number;
  eventHandlers?: LeafletEventHandlerFnMap;
}

const Arrow = ({
  positions,
  color,
  opacity = 1,
  isHighlighted,
  easyClickTolerance,
  id,
  eventHandlers,
  zoomLevel,
  interactive,
}: ArrowProps) => {
  const end = positions[positions.length - 1];

  const getArrowHead = (positions: LatLng[]): LatLngExpression[] => {
    const tip = positions[1];
    const tail = positions[0];

    const tipPoint = point([tip.lng, tip.lat]);
    const tailPoint = point([tail.lng, tail.lat]);

    const arrowHeadSize = zoomLevel ? 70 * Math.pow(2, 14 - zoomLevel) : 70;
    const options: { units: Units } = { units: "meters" };

    // Bearing from tail to tip
    const mainBearing = bearing(tailPoint, tipPoint);

    // Move back from tip to get the base center
    const baseCenter = destination(tipPoint, arrowHeadSize, mainBearing + 180, options);

    // Calculate base points at ±60° from the main bearing, starting from base center
    const baseBearings = [mainBearing + 120, mainBearing - 120];
    const basePoints = baseBearings.map((b) => {
      const pt = destination(baseCenter, arrowHeadSize, b, options);
      return [pt.geometry.coordinates[1], pt.geometry.coordinates[0]];
    });

    // Return triangle: tip, basePoint1, basePoint2
    return [[tip.lat, tip.lng] as [number, number], ...(basePoints as [number, number][])];
  };

  const arrowColor = isHighlighted ? BLUE_HIGHLIGHT : color || DEFAULT_ANNOTATION_COLOR;

  return (
    <>
      <Polyline
        key={`${id ?? ""}-arrow-body`}
        positions={positions}
        pathOptions={{
          color: arrowColor,
          weight: ShapeLineWidth,
          opacity: opacity,
        }}
        interactive={interactive}
        eventHandlers={eventHandlers}
      />
      <Polyline
        key={`${id ?? ""}-arrow-selector`}
        positions={positions}
        pathOptions={{
          color: "black",
          weight: ShapeLineWidth + 2 * easyClickTolerance,
          opacity: 0,
        }}
        interactive={interactive}
        eventHandlers={eventHandlers}
      />
      {end && (
        <Polygon
          key={`${id ?? ""}-arrow-head`}
          positions={getArrowHead(positions)}
          pathOptions={{
            color: arrowColor,
            opacity: opacity,
            fill: true,
            fillColor: arrowColor,
            fillOpacity: opacity,
          }}
          interactive={interactive}
          eventHandlers={eventHandlers}
        />
      )}
    </>
  );
};

export default Arrow;
